import { api } from './client';

/**
 * ============================================================================
 * THE PROFILE CLIENT — the contacts, and the CUTOVER UNLOCK behind them
 * ============================================================================
 *
 *   GET  /pa/profile/contacts                  — the label/value list (§4, F4)
 *   POST /pa/profile/contacts/{kind}           — set ONE contact; the answer is the
 *                                                fresh list, because the write RESETS
 *                                                confirmation and the screen must render
 *                                                the server's new state and not its own
 *   POST /pa/profile/contacts/{kind}/code      — send a code TO THE CARD's own contact
 *   POST /pa/profile/contacts/{kind}/confirm   — submit it; the answer is the fresh list
 *
 * It mirrors the wire structs in `backend/internal/personalarea/profile_handlers.go`,
 * pinned BOTH WAYS to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ⚠ WHY THIS IS THE MOST IMPORTANT SCREEN OF THE WAVE, stated here because a later
 * reader will take it for a convenience. `subscriber.email_confirmed` and
 * `subscriber.mobile_confirmed` are `NOT NULL DEFAULT false`, and the notification engine
 * refuses an UNCONFIRMED contact per channel — so an imported subscriber who presses
 * «Забыли пароль?» reads «пароль отправлен» and receives nothing, and receives no
 * suspension notice, no payment confirmation and no dunning warning either. Until he
 * confirms a contact here he is a telephone call to the operator.
 *
 * ⚠ `POST …/{kind}/code` TAKES NO ADDRESS, DELIBERATELY, and this client must never grow
 * one. The destination is read off the subscriber's own card by the server, so the route
 * cannot be used to send the operator's SMS to an arbitrary number at his expense. That
 * is also why the SCREEN renders no contact field on the code step: a field whose value
 * the server drops is the D109 class, and here it would be a field that looks like it
 * chooses where a code goes.
 *
 * ⚠ `phone` IS DISPLAY-ONLY AND THERE IS NO WRITER FOR IT. It has no confirmation column
 * and nothing is ever delivered to it, so an editor over it would be a control with no
 * consequence.
 */

/** The two contact kinds the server admits. `phone` is not one of them — see the header. */
export type PAContactKind = 'mobile' | 'email';

/** `PAContact` — one contact value and whether its possession has been proved. */
export interface PAContact {
  /** `""` when nothing is recorded. */
  value: string;
  /**
   * ⚠ THE DELIVERY PRECONDITION, not a decoration. An unconfirmed contact receives
   * NOTHING — not a rotated password, not a suspension notice, not a payment
   * confirmation. False is the default of every imported card.
   */
  confirmed: boolean;
}

/** `PAContacts` — the profile screen's whole label/value list. */
export interface PAContacts {
  display_name: string;
  /** The PORTAL login, not an Account's. */
  login: string;
  /** The card's contact/landline number. DISPLAY ONLY — see the header. */
  phone: string;
  mobile: PAContact;
  email: PAContact;
  /**
   * Whether this installation can send anything at all. FALSE means both code routes
   * answer 503, so the screen must not render «Подтвердить» — a button whose only
   * possible answer is an error, on the one screen whose purpose is to make delivery
   * work.
   */
  notifier_available: boolean;
}

/** `PAContactValueRequest`. An EMPTY value is refused by the server and is not a delete. */
export interface PAContactValueRequest {
  value: string;
  /**
   * ⚠ THE SUBSCRIBER'S OWN PORTAL PASSWORD, AND A SESSION IS NO LONGER ENOUGH TO MOVE A
   * CONTACT. Required by the server when the value CHANGES; ignored when it is the same, so a
   * re-save of an unchanged value must not prompt for it (the spec states that as a rule on
   * this client, not as an implementation note).
   *
   * It exists because a session alone was a full account takeover: writing a contact resets
   * its confirmation, the confirmation code necessarily goes to the NEW address, and the
   * unauthenticated recovery route then mails the plaintext password there and revokes the
   * real subscriber's sessions. So this write is held to the guarantee the password form
   * already makes — a session stolen from a shared browser must not be able to move the
   * credential.
   *
   * A mismatch answers `401 pa.current_password_invalid` with `details.field =
   * "current_password"` and is counted against the durable login lockout.
   *
   * ⚠ ~~`pa.invalid_credentials`~~ UNTIL 2026-08-12 (the second review fix pass), and the strike
   * is kept because it states the rule: that code is the LOGIN route's, its Russian message is
   * «Неверный логин или пароль», and this form has no login field. The vagueness is a security
   * property THERE — six causes must be indistinguishable or the form enumerates logins — and it
   * buys nothing here, where the caller already holds a session and submits one value. The three
   * AUTHENTICATED re-verification forms (this one, `POST /pa/auth/password` and
   * `POST /pa/accounts/{id}/password`) now share `pa.current_password_invalid`, byte-identical
   * between them and still counted against the same lockout.
   */
  current_password: string;
}

/** `PAContactCode` — confirmation that a code is in flight. The code is never returned. */
export interface PAContactCode {
  kind: string;
  /** The channel it went out on («sms», «email»), decided by the kind. */
  channel: string;
  /** When the code dies — so the screen states the server's deadline, not a guess at it. */
  expires_at: string;
}

/** `PAContactCodeRequest`. */
export interface PAContactCodeRequest {
  code: string;
}

export function getContacts(): Promise<PAContacts> {
  return api.get<PAContacts>('/pa/profile/contacts');
}

/**
 * setContact writes ONE column and returns the FRESH list.
 *
 * ⚠ THE RESPONSE IS THE ONLY TRUTH ABOUT WHAT HAPPENED, and the screen renders it rather
 * than the value it sent. Two reasons, both live: changing the value RESETS confirmation
 * (F4), so an optimistic screen would keep a green «Подтверждён» beside a contact that no
 * longer has one; and the server stores what it stores — a re-save of the SAME value
 * keeps the confirmation, which no client-side rule can know.
 */
export function setContact(
  kind: PAContactKind,
  value: string,
  currentPassword: string,
): Promise<PAContacts> {
  const body: PAContactValueRequest = { value, current_password: currentPassword };
  return api.post<PAContacts>(`/pa/profile/contacts/${kind}`, body);
}

/**
 * sendContactCode asks for a code to be sent to the contact ON THE CARD.
 *
 * ⚠ IT SENDS NO BODY AT ALL, and that is the contract rather than an omission — see this
 * file's header. `ProfileScreen.test.tsx` asserts the recorded request body is
 * `undefined`, so a later "improvement" that passed the address along would fail rather
 * than quietly create a channel the server ignores.
 */
export function sendContactCode(kind: PAContactKind): Promise<PAContactCode> {
  return api.post<PAContactCode>(`/pa/profile/contacts/${kind}/code`);
}

export function confirmContact(kind: PAContactKind, code: string): Promise<PAContacts> {
  const body: PAContactCodeRequest = { code };
  return api.post<PAContacts>(`/pa/profile/contacts/${kind}/confirm`, body);
}
